/**
 * Recapro Landing Page - Vanilla JavaScript
 * Interactions. The scroll/reveal motion layer lives in ./anim.js.
 */
import gsap from 'gsap';
import { initAnimations } from './anim.js';

// ===========================================
// Language Banner - Suggest French to French-speaking users
// ===========================================
(function () {
  // Only run on English pages
  if (!window.location.pathname.startsWith('/en')) return;

  // Check if user already dismissed the banner
  if (localStorage.getItem('lang-banner-dismissed')) return;

  // Detect French browser
  var lang = navigator.language || navigator.userLanguage || 'en';
  if (!lang.toLowerCase().startsWith('fr')) return;

  // Build French equivalent URL from hreflang alternate (dynamic, no hardcoded slug map)
  var frAlt = document.querySelector('link[rel="alternate"][hreflang="fr-FR"]');
  if (!frAlt) return;
  var frFullUrl = new URL(frAlt.href);
  var frUrl = frFullUrl.pathname + window.location.search + window.location.hash;

  // Inject banner when DOM is ready
  function showBanner() {
    var banner = document.createElement('div');
    banner.id = 'lang-banner';
    banner.setAttribute('role', 'banner');
    banner.style.cssText =
      'position:fixed;bottom:0;left:0;right:0;z-index:9999;background:#323A45;color:#fff;padding:12px 16px;display:flex;align-items:center;justify-content:center;gap:12px;font-size:14px;font-family:Inter,sans-serif;box-shadow:0 -2px 8px rgba(0,0,0,0.15);';

    var text = document.createElement('span');
    text.textContent = 'Cette page est disponible en fran\u00e7ais';

    var link = document.createElement('a');
    link.href = frUrl;
    link.textContent = 'Voir en fran\u00e7ais';
    link.style.cssText =
      'color:#fff;background:#0832b2;padding:6px 14px;border-radius:8px;text-decoration:none;font-weight:600;white-space:nowrap;';

    var dismiss = document.createElement('button');
    dismiss.textContent = '\u2715';
    dismiss.setAttribute('aria-label', 'Fermer');
    dismiss.style.cssText =
      'background:none;border:none;color:#fff;font-size:18px;cursor:pointer;padding:4px 8px;opacity:0.7;';
    dismiss.addEventListener('click', function () {
      banner.remove();
      localStorage.setItem('lang-banner-dismissed', '1');
    });

    banner.appendChild(text);
    banner.appendChild(link);
    banner.appendChild(dismiss);
    document.body.appendChild(banner);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', showBanner);
  } else {
    showBanner();
  }
})();

document.addEventListener('DOMContentLoaded', () => {
  injectWebManifest();
  initMobileMenu();
  initDropdowns();
  initHeaderScrollState();
  initCardGlowEffect();
  initPipelineSequence();
  initEcosystemFlowAnimation();
  initDeclarativeTracking();
  initAnimations();
});

// ===========================================
// Web Manifest (injected at runtime to avoid Parcel resolution)
// ===========================================
function injectWebManifest() {
  const link = document.createElement('link');
  link.rel = 'manifest';
  link.href = '/site.webmanifest';
  document.head.appendChild(link);
}

// ===========================================
// Mobile Menu Toggle
// ===========================================
function initMobileMenu() {
  const menuBtn = document.getElementById('mobile-menu-btn');
  const closeBtn = document.getElementById('mobile-menu-close');
  const overlay = document.getElementById('mobile-menu');

  if (!menuBtn || !closeBtn || !overlay) return;

  const openMenu = () => {
    overlay.classList.add('open');
    menuBtn.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
  };

  const closeMenu = () => {
    overlay.classList.remove('open');
    menuBtn.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
  };

  // Expose globally for inline onclick handlers in _header.ejs
  window.closeMobileMenu = closeMenu;

  menuBtn.addEventListener('click', openMenu);
  closeBtn.addEventListener('click', closeMenu);

  overlay.addEventListener('click', e => {
    if (e.target === overlay || e.target.id === 'mobile-menu-backdrop') {
      closeMenu();
    }
  });

  document.addEventListener('keydown', e => {
    if (e.key === 'Escape' && overlay.classList.contains('open')) {
      closeMenu();
    }
  });

  const menuLinks = overlay.querySelectorAll('a');
  menuLinks.forEach(link => {
    link.addEventListener('click', closeMenu);
  });
}

// ===========================================
// Mobile Dropdown Toggles
// ===========================================
function initDropdowns() {
  const toggles = document.querySelectorAll('.mobile-dropdown-toggle');

  toggles.forEach(toggle => {
    toggle.addEventListener('click', () => {
      const panel = toggle.nextElementSibling;
      if (!panel) return;

      const isOpen = toggle.getAttribute('aria-expanded') === 'true';

      if (isOpen) {
        panel.style.maxHeight = '0';
        toggle.setAttribute('aria-expanded', 'false');
      } else {
        panel.style.maxHeight = panel.scrollHeight + 'px';
        toggle.setAttribute('aria-expanded', 'true');
      }
    });
  });
}

// ===========================================
// Pipeline section: auto-advancing active highlight across the 5 steps.
// Starts when the section scrolls into view; stops when it leaves. Gated on
// reduced-motion preference.
// ===========================================
function initPipelineSequence() {
  const steps = Array.from(document.querySelectorAll('.pipe-step'));
  if (!steps.length) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  let active = 0;
  let timer = null;
  const advance = () => {
    steps[active].classList.remove('is-active');
    active = (active + 1) % steps.length;
    steps[active].classList.add('is-active');
  };
  const start = () => {
    if (timer === null) {
      steps[active].classList.add('is-active');
      timer = window.setInterval(advance, 2400);
    }
  };
  const stop = () => {
    if (timer !== null) {
      window.clearInterval(timer);
      timer = null;
    }
  };
  const section = steps[0].closest('section');
  if (section === null) {
    start();
    return;
  }
  const observer = new IntersectionObserver(
    entries => {
      for (const entry of entries) {
        if (entry.isIntersecting) start();
        else stop();
      }
    },
    { threshold: 0.25 }
  );
  observer.observe(section);
}

// ===========================================
// Header Scroll State
// ===========================================
function initHeaderScrollState() {
  const header = document.querySelector('header');

  if (!header) return;

  let ticking = false;

  const updateHeader = () => {
    if (window.scrollY > 50) {
      header.classList.add('header-scrolled');
    } else {
      header.classList.remove('header-scrolled');
    }
    ticking = false;
  };

  window.addEventListener('scroll', () => {
    if (!ticking) {
      requestAnimationFrame(updateHeader);
      ticking = true;
    }
  });

  updateHeader();
}

// ===========================================
// Card Glow Effect (Mouse Tracking)
// ===========================================
function initCardGlowEffect() {
  const cards = document.querySelectorAll('.card-glow, .card-glow-warning, .card-glow-success');

  if (!cards.length) return;

  cards.forEach(card => {
    card.addEventListener('mousemove', e => {
      const rect = card.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      card.style.setProperty('--mouse-x', `${x}%`);
      card.style.setProperty('--mouse-y', `${y}%`);
    });
  });
}

// ===========================================
// Ecosystem Flow Animation
// ===========================================
function initEcosystemFlowAnimation() {
  const container = document.getElementById('ecosystem-flow');
  if (!container) return;

  const inputs = container.querySelectorAll('.ecosystem-input');
  const outputs = container.querySelectorAll('.ecosystem-output');
  const processingCard = container.querySelector('.ecosystem-processing');
  const logo = container.querySelector('.ecosystem-logo');
  const phaseIndicators = container.querySelectorAll('.ecosystem-phase');
  const hostingBtns = container.querySelectorAll('.hosting-btn');
  const arrowsSvg = document.getElementById('ecosystem-arrows');
  const inputArrowsGroup = document.getElementById('input-arrows');
  const outputArrowsGroup = document.getElementById('output-arrows');
  const gridContainer = container.querySelector('.grid');

  if (!inputs.length || !outputs.length) return;

  // One timeline segment per phase, each holding its state for the same 2.5s the
  // setInterval gave it. GSAP owns the CADENCE; the markup's own
  // `transition-all duration-500` still owns the EASING of every visual state.
  // The states below therefore stay plain inline-style writes: tweening the same
  // properties in GSAP would drive them twice and change every rendered
  // mid-state, which is exactly what this migration must not do.
  const PHASE_COUNT = 3;
  const PHASE_DURATION = 2.5;
  const RESUME_DELAY = 5;
  const ARROW_DELAY = 0.1;

  let currentPhase = 0;
  let isCloud = true;
  let arrowCall = null;

  // Draw ephemeral arrows from inputs to processing card
  const drawInputArrows = () => {
    if (!inputArrowsGroup || !processingCard || !container || !arrowsSvg) return;

    // Clear previous arrows
    inputArrowsGroup.innerHTML = '';

    // Use the ecosystem-flow container's bounding rect as reference
    const containerRect = container.getBoundingClientRect();
    const processingRect = processingCard.getBoundingClientRect();

    // Update SVG viewBox to match container dimensions
    const svgWidth = containerRect.width;
    const svgHeight = containerRect.height;
    arrowsSvg.setAttribute('viewBox', `0 0 ${svgWidth} ${svgHeight}`);

    const targetX = processingRect.left - containerRect.left;
    const targetY = processingRect.top - containerRect.top + processingRect.height / 2;

    inputs.forEach((input, index) => {
      const inputRect = input.getBoundingClientRect();
      const startX = inputRect.right - containerRect.left + 8;
      const startY = inputRect.top - containerRect.top + inputRect.height / 2;

      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');

      // Create a smooth bezier curve from input to processing card
      const midX = startX + (targetX - startX) * 0.5;
      const d = `M ${startX} ${startY} C ${midX} ${startY}, ${midX} ${targetY}, ${targetX} ${targetY}`;

      path.setAttribute('d', d);
      path.setAttribute('stroke', '#0832b2');
      path.setAttribute('stroke-width', '2');
      path.setAttribute('fill', 'none');
      path.setAttribute('stroke-dasharray', '8,4');
      path.setAttribute('marker-end', 'url(#arrowhead-brand)');
      path.classList.add('ecosystem-arrow-path');
      path.style.animationDelay = `${index * 100}ms`;
      inputArrowsGroup.appendChild(path);
    });

    // Show the group - use both class and inline style for reliability
    inputArrowsGroup.classList.remove('opacity-0');
    inputArrowsGroup.classList.add('opacity-100');
    inputArrowsGroup.style.opacity = '1';
  };

  // Draw ephemeral arrows from processing card to outputs
  const drawOutputArrows = () => {
    if (!outputArrowsGroup || !processingCard || !container || !arrowsSvg) return;

    // Clear previous arrows
    outputArrowsGroup.innerHTML = '';

    // Use the ecosystem-flow container's bounding rect as reference
    const containerRect = container.getBoundingClientRect();
    const processingRect = processingCard.getBoundingClientRect();

    // Update SVG viewBox to match container dimensions
    const svgWidth = containerRect.width;
    const svgHeight = containerRect.height;
    arrowsSvg.setAttribute('viewBox', `0 0 ${svgWidth} ${svgHeight}`);

    const startX = processingRect.right - containerRect.left;
    const startY = processingRect.top - containerRect.top + processingRect.height / 2;

    outputs.forEach((output, index) => {
      const outputRect = output.getBoundingClientRect();
      const targetX = outputRect.left - containerRect.left - 8;
      const targetY = outputRect.top - containerRect.top + outputRect.height / 2;

      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');

      // Create a smooth bezier curve from processing card to output
      const midX = startX + (targetX - startX) * 0.5;
      const d = `M ${startX} ${startY} C ${midX} ${startY}, ${midX} ${targetY}, ${targetX} ${targetY}`;

      path.setAttribute('d', d);
      path.setAttribute('stroke', '#1dd1a1');
      path.setAttribute('stroke-width', '2');
      path.setAttribute('fill', 'none');
      path.setAttribute('stroke-dasharray', '8,4');
      path.setAttribute('marker-end', 'url(#arrowhead-success)');
      path.classList.add('ecosystem-arrow-path');
      path.style.animationDelay = `${index * 100}ms`;
      outputArrowsGroup.appendChild(path);
    });

    // Show the group - use both class and inline style for reliability
    outputArrowsGroup.classList.remove('opacity-0');
    outputArrowsGroup.classList.add('opacity-100');
    outputArrowsGroup.style.opacity = '1';
  };

  // Clear all ephemeral arrows
  const clearArrows = () => {
    // A pending draw from the previous phase would otherwise land after this
    // clear and leave arrows on screen for a phase that does not own them.
    if (arrowCall !== null) {
      arrowCall.kill();
      arrowCall = null;
    }
    if (inputArrowsGroup) {
      inputArrowsGroup.innerHTML = '';
      inputArrowsGroup.classList.remove('opacity-100');
      inputArrowsGroup.classList.add('opacity-0');
      inputArrowsGroup.style.opacity = '0';
    }
    if (outputArrowsGroup) {
      outputArrowsGroup.innerHTML = '';
      outputArrowsGroup.classList.remove('opacity-100');
      outputArrowsGroup.classList.add('opacity-0');
      outputArrowsGroup.style.opacity = '0';
    }
  };

  const updatePhaseDisplay = () => {
    // Reset all states first
    clearArrows();

    inputs.forEach(input => {
      input.style.transform = '';
      input.style.backgroundColor = '';
      input.style.borderColor = '';
      const pulse = input.querySelector('.ecosystem-pulse');
      if (pulse) pulse.style.opacity = '0';
    });

    outputs.forEach(output => {
      output.style.transform = '';
      output.style.backgroundColor = '';
      output.style.borderColor = '';
      const pulse = output.querySelector('.ecosystem-pulse');
      if (pulse) pulse.style.opacity = '0';
    });

    if (processingCard) {
      processingCard.style.borderColor = '';
      processingCard.style.transform = '';
    }
    if (logo) {
      logo.style.transform = '';
    }

    // Phase 0: Collecte - highlight inputs and draw arrows to processing
    if (currentPhase === 0) {
      inputs.forEach(input => {
        input.style.transform = 'translateX(0.5rem)';
        input.style.backgroundColor = 'rgba(8, 50, 178, 0.1)';
        input.style.borderColor = 'rgba(8, 50, 178, 0.3)';
        const pulse = input.querySelector('.ecosystem-pulse');
        if (pulse) pulse.style.opacity = '1';
      });
      // Draw ephemeral input arrows
      arrowCall = gsap.delayedCall(ARROW_DELAY, drawInputArrows);
    }

    // Phase 1: Traitement - highlight processing
    if (currentPhase === 1) {
      if (processingCard) {
        processingCard.style.borderColor = '#1dd1a1';
        processingCard.style.transform = 'scale(1.02)';
      }
      if (logo) {
        logo.style.transform = 'scale(1.1)';
      }
    }

    // Phase 2: Distribution - highlight outputs and draw arrows from processing
    if (currentPhase === 2) {
      outputs.forEach(output => {
        output.style.transform = 'translateX(-0.5rem)';
        output.style.backgroundColor = 'rgba(29, 209, 161, 0.1)';
        output.style.borderColor = 'rgba(29, 209, 161, 0.3)';
        const pulse = output.querySelector('.ecosystem-pulse');
        if (pulse) pulse.style.opacity = '1';
      });
      // Draw ephemeral output arrows
      arrowCall = gsap.delayedCall(ARROW_DELAY, drawOutputArrows);
    }

    // Update phase indicators
    phaseIndicators.forEach((indicator, index) => {
      const dot = indicator.querySelector('div');
      if (index === currentPhase) {
        indicator.style.backgroundColor = 'rgba(8, 50, 178, 0.1)';
        indicator.style.color = '#0832b2';
        if (dot) {
          dot.style.backgroundColor = '#0832b2';
          dot.style.transform = 'scale(1.5)';
        }
      } else {
        indicator.style.backgroundColor = '';
        indicator.style.color = '';
        if (dot) {
          dot.style.backgroundColor = '';
          dot.style.transform = '';
        }
      }
    });
  };

  const renderPhase = phase => {
    currentPhase = phase;
    updatePhaseDisplay();
  };

  const updateHostingToggle = () => {
    hostingBtns.forEach(btn => {
      const hosting = btn.dataset.hosting;
      const isActive = (hosting === 'cloud' && isCloud) || (hosting === 'onprem' && !isCloud);

      if (isActive) {
        btn.classList.add('bg-brand-500/10', 'border-brand-500', 'text-brand-500');
        btn.classList.remove('bg-background-soft', 'border-line-subtle', 'text-ink-muted');
        btn.style.borderWidth = '2px';
        btn.setAttribute('aria-pressed', 'true');
      } else {
        btn.classList.remove('bg-brand-500/10', 'border-brand-500', 'text-brand-500');
        btn.classList.add('bg-background-soft', 'border-line-subtle', 'text-ink-muted');
        btn.style.borderWidth = '1px';
        btn.setAttribute('aria-pressed', 'false');
      }
    });
  };

  // The loop: phase 1 and 2 are scheduled inside the iteration, the return to
  // phase 0 rides onRepeat, which never fires on the first play, so the initial
  // paint below stays a plain render and does not flip the hosting mode.
  const phaseLoop = gsap.timeline({
    repeat: -1,
    paused: true,
    onRepeat: () => {
      renderPhase(0);
      isCloud = isCloud === false;
      updateHostingToggle();
    },
  });
  phaseLoop.call(renderPhase, [1], PHASE_DURATION);
  phaseLoop.call(renderPhase, [2], PHASE_DURATION * 2);
  phaseLoop.to({}, { duration: PHASE_COUNT * PHASE_DURATION }, 0);

  // Click handlers for phase indicators
  phaseIndicators.forEach(indicator => {
    indicator.addEventListener('click', () => {
      const phase = parseInt(indicator.dataset.phase, 10);
      phaseLoop.pause();
      renderPhase(phase);
      // Resume where the clicked phase sits on the timeline, so the loop picks
      // up at the NEXT phase rather than replaying the one just selected. seek()
      // suppresses callbacks, so the jump itself paints nothing.
      gsap.delayedCall(RESUME_DELAY, () => {
        phaseLoop.seek(currentPhase * PHASE_DURATION).play();
      });
    });
  });

  // Click handlers for hosting buttons
  hostingBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      const hosting = btn.dataset.hosting;
      isCloud = hosting === 'cloud';
      updateHostingToggle();
    });
  });

  // Redraw arrows on resize
  window.addEventListener('resize', () => {
    if (currentPhase === 0) {
      drawInputArrows();
    } else if (currentPhase === 2) {
      drawOutputArrows();
    }
  });

  // Initialize
  updateHostingToggle();
  updatePhaseDisplay();
  // The auto-advance is the only motion here: the widget stays fully readable
  // and clickable without it. Same gate as initPipelineSequence.
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches === false) {
    phaseLoop.play();
  }
}

// ===========================================
// Declarative Tracking (Matomo)
// ===========================================
function initDeclarativeTracking() {
  document.addEventListener('change', event => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (target.dataset.trkEventOn !== 'change') return;
    if (!target.dataset.trkEventCategory || !window._paq) return;
    window._paq.push([
      'trackEvent',
      target.dataset.trkEventCategory,
      target.dataset.trkEventAction || 'change',
      target.dataset.trkEventName || '',
      0,
    ]);
  });

  document.addEventListener('click', event => {
    const target = event.target;
    const trackableElement = target.closest('[data-trk-event-category]');
    if (!trackableElement) return;
    if (trackableElement.dataset.trkEventOn === 'change') return;

    const category = trackableElement.dataset.trkEventCategory;
    const action = trackableElement.dataset.trkEventAction || 'click';
    const name = trackableElement.dataset.trkEventName;
    const value = trackableElement.dataset.trkEventValue
      ? parseInt(trackableElement.dataset.trkEventValue, 10)
      : undefined;

    if (category && window._paq) {
      window._paq.push(['trackEvent', category, action, name || '', value || 0]);
    }
  });
}

// Anchor scrolling is NOT handled here any more. Under Lenis it lives in
// anim.js (which also re-applies the header offset Lenis cannot read from CSS);
// under prefers-reduced-motion no handler is installed at all, so fragment
// navigation stays native and honours `scroll-mt-24` on its own.
